import { reactive, onBeforeUnmount } from "vue";
import { ScanKeyEvent, Shopflow, SearchUHFEvent, PLUGIN_EVENTS, EVENTS, useDeviceInfo } from "./index";

const general_state = reactive<{
  // состояние поиска
  search_is_running: boolean;
  // состояние прослушивания событий нажатия клавиш на устройстве (false - события не слушаются, true - события слушаются)
  subscribed_to_device_key_events: boolean;
  // состояние прослушивания событий от плагина (false - события не слушаются, true - события слушаются)
  subscribed_to_plugin: boolean;
  // состояние отправки событий в приложение (false - события не отправляются, true - события отправляются)
  broadcast: boolean;
  // фильтр для поиска uhf
  filter: string | null;
  // callback для отписки от прослушивания событий от плагина
  unsubscribe_to_plugin_callback: () => Promise<void> | null;
  // callback для отписки от прослушивания событий нажатия клавиш на устройстве
  unsubscribe_to_device_key_events_callback: () => void | null;
}>({
  search_is_running: false,
  subscribed_to_device_key_events: false,
  subscribed_to_plugin: false,
  broadcast: false,
  filter: null,
  unsubscribe_to_plugin_callback: null,
  unsubscribe_to_device_key_events_callback: null,
});

export const useSearchUHF = () => {
  const { deviceInfo } = useDeviceInfo();

  const local_state = reactive<{
    // состояние прослушивания событий поиска (false - события не слушаются, true - события слушаются)
    subscribed_to_search_uhf: boolean;
    // callback для отписки от прослушивания событий поиска
    unsubscribe_to_search_uhf_callback: () => void | null;
  }>({
    subscribed_to_search_uhf: false,
    unsubscribe_to_search_uhf_callback: null,
  });

  // проверка устройства на наличие rfid
  const isRfidReader = () => Boolean(deviceInfo.value?.isRfidReader);

  /**
   * отправка события в приложение
   * @param event - событие полученное от плагина
   */
  const broadcast = (event: SearchUHFEvent) => {
    if (!isRfidReader()) return;
    if (!general_state.broadcast) return;
    if (!general_state.search_is_running) return;

    window.dispatchEvent(new CustomEvent(EVENTS.SEARCH_UHF_EVENT, { detail: event }));
  };

  /**
   * установить фильтр для поиска
   * @param filter - фильтр для поиска
   */
  const setFilterForSearch = (filter: string) => {
    if (!isRfidReader()) return;

    general_state.filter = filter;
  };

  // сбросить фильтр для поиска
  const resetFilterForSearch = () => {
    if (!isRfidReader()) return;

    general_state.filter = null;
  };

  // подписаться на событие плагина
  const subscribeToPluginEvent = async () => {
    if (!isRfidReader()) return;
    if (general_state.subscribed_to_plugin) return;

    const { remove } = await Shopflow.addListener(PLUGIN_EVENTS.SEARCH_UHF_EVENT, ({ value }) => {
      broadcast({ value });
    });

    general_state.subscribed_to_plugin = true;
    general_state.unsubscribe_to_plugin_callback = remove;
  };

  // отписаться от событий плагина
  const unsubscribeToPluginEvent = () => {
    if (!isRfidReader()) return;
    if (!general_state.unsubscribe_to_plugin_callback) return;

    general_state.unsubscribe_to_plugin_callback();
    general_state.unsubscribe_to_plugin_callback = null;
    general_state.subscribed_to_plugin = false;
  };

  // включить отправку событий в приложение
  const enableBroadcast = () => {
    if (!isRfidReader()) return;
    if (general_state.broadcast) return;

    general_state.broadcast = true;
  };

  // выключить отправку событий в приложение
  const disabledBroadcast = () => {
    if (!isRfidReader()) return;

    general_state.broadcast = false;
  };

  /**
   * подписаться на события поиска uhf
   * @param callback - callback, который будет вызываться при получении события
   */
  const subscribeToSearchUHF = (callback: (event: SearchUHFEvent) => void) => {
    if (!isRfidReader()) return;
    if (local_state.subscribed_to_search_uhf) return;

    const listener = (event: CustomEvent<SearchUHFEvent>) => {
      callback(event.detail);
    };

    window.addEventListener(EVENTS.SEARCH_UHF_EVENT, listener);

    local_state.subscribed_to_search_uhf = true;
    local_state.unsubscribe_to_search_uhf_callback = () => {
      window.removeEventListener(EVENTS.SEARCH_UHF_EVENT, listener);
    };
  };

  // отписаться от событий поиска uhf
  const unsubscribeToSearchUHF = () => {
    if (!isRfidReader()) return;
    if (!local_state.unsubscribe_to_search_uhf_callback) return;

    local_state.unsubscribe_to_search_uhf_callback();
    local_state.unsubscribe_to_search_uhf_callback = null;
    local_state.subscribed_to_search_uhf = false;
  };

  // подписаться на события нажатия клавиш на устройстве: первое нажатие — старт поиска, второе — стоп
  const subscribeToDeviceKeyEvents = () => {
    if (!isRfidReader()) return;
    if (!general_state.filter) return;
    if (general_state.subscribed_to_device_key_events) return;

    const listener = async (event: CustomEvent<ScanKeyEvent>) => {
      if (event.detail.code !== deviceInfo.value.rfidGunKeyCode) return;
      if (event.detail.action !== "up") return;

      if (general_state.search_is_running) {
        general_state.search_is_running = false;
        await Shopflow.stopSearchUHF();
      } else {
        general_state.search_is_running = true;
        await Shopflow.startSearchUHF({ filter: general_state.filter });
      }
    };

    window.addEventListener(EVENTS.SCAN_KEY_EVENT, listener);

    general_state.unsubscribe_to_device_key_events_callback = () => {
      window.removeEventListener(EVENTS.SCAN_KEY_EVENT, listener);
    };
  };

  // отписаться от событий нажатия клавиш на устройстве
  const unsubscribeToDeviceKeyEvents = async () => {
    if (!isRfidReader()) return;
    if (!general_state.unsubscribe_to_device_key_events_callback) return;

    if (general_state.search_is_running) {
      await Shopflow.stopSearchUHF();
      general_state.search_is_running = false;
    }

    general_state.unsubscribe_to_device_key_events_callback();
    general_state.unsubscribe_to_device_key_events_callback = null;
    general_state.subscribed_to_device_key_events = false;
  };

  onBeforeUnmount(() => {
    if (!local_state.subscribed_to_search_uhf) return;

    unsubscribeToSearchUHF();
  });

  return {
    state: reactive(general_state),
    setFilterForSearch,
    resetFilterForSearch,
    subscribeToPluginEvent,
    unsubscribeToPluginEvent,
    enableBroadcast,
    disabledBroadcast,
    subscribeToSearchUHF,
    unsubscribeToSearchUHF,
    subscribeToDeviceKeyEvents,
    unsubscribeToDeviceKeyEvents,
  };
};
